import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';

const parseShkIds = (text) => {
  if (!text || !String(text).trim()) return [];
  const parts = String(text)
    .split(/[\n,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const ids = [];
  for (const p of parts) {
    const n = parseInt(p, 10);
    if (!Number.isNaN(n)) ids.push(n);
  }
  return ids;
};

export default function FindReturnByShkModal({ show, onHide, onEditOrder }) {
  const { t } = useLanguage();
  const [shkIdsText, setShkIdsText] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const handleSubmit = async () => {
    const shkIds = parseShkIds(shkIdsText);
    if (shkIds.length === 0) {
      setError(t('findReturnByShk.noShkIds'));
      return;
    }
    setError('');
    setLoading(true);
    setResults(null);
    try {
      const res = await api.postAdminFindReturnsByShk(shkIds);
      setResults(res.results || []);
    } catch (err) {
      setError(err.data?.errorText || err.message || 'Request failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrder = async (orderId, shkId) => {
    if (!window.confirm(t('findReturnByShk.confirmDelete'))) return;
    setDeletingId(orderId);
    try {
      await api.deleteOrder(orderId);
      setResults((prev) =>
        (prev || []).map((r) =>
          r.order_id === orderId
            ? {
                ...r,
                order_id: null,
                platform_order_no: null,
                store_id: null,
                store_name: '',
                store_wb_id: '',
                created: false,
                error: 'deleted',
              }
            : r
        )
      );
    } catch (err) {
      setError(err.data?.errorText || err.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const handleClose = () => {
    setShkIdsText('');
    setResults(null);
    setError('');
    onHide();
  };

  useEffect(() => {
    if (!show) return;
    setShkIdsText('');
    setResults(null);
    setError('');
  }, [show]);

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" className="find-return-by-shk-modal">
      <Modal.Header closeButton>
        <Modal.Title style={{ color: 'var(--bs-body-color)' }}>{t('findReturnByShk.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--card-bg)' }}>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label>shkId (WB goods-returns)</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            placeholder={t('findReturnByShk.shkIdsPlaceholder')}
            value={shkIdsText}
            onChange={(e) => setShkIdsText(e.target.value)}
            className="find-return-by-shk-input"
          />
          <Form.Text className="text-muted">{t('findReturnByShk.shkIdsHint')}</Form.Text>
        </Form.Group>

        {results && results.length > 0 && (
          <div className="mt-3 find-return-by-shk-results" style={{ color: 'var(--bs-body-color)' }}>
            <h6 style={{ color: 'var(--bs-body-color)' }}>{t('findReturnByShk.resultsTitle')}</h6>
            {results.map((r, idx) => (
              <div
                key={r.shk_id != null ? `${r.shk_id}-${idx}` : idx}
                className="border rounded p-3 mb-2 find-return-by-shk-result-item d-flex flex-wrap align-items-center justify-content-between gap-2"
              >
                <div className="flex-grow-1">
                  <strong>shkId: {r.shk_id}</strong>
                  {r.error === 'not_found' && (
                    <span className="text-warning ms-2">({t('findReturnByShk.notFound')})</span>
                  )}
                  {r.error && r.error !== 'not_found' && r.error !== 'deleted' && (
                    <span className="text-danger ms-2">— {r.error}</span>
                  )}
                  {r.error === 'deleted' && (
                    <span className="text-muted ms-2">({t('findReturnByShk.deleted')})</span>
                  )}
                  {r.order_id && r.error !== 'deleted' && (
                    <>
                      <div className="small mt-1">
                        {r.store_name} {r.store_wb_id && `(${r.store_wb_id})`}
                      </div>
                      <div className="small text-muted">srid: {r.platform_order_no}</div>
                      {r.created && (
                        <span className="badge bg-success ms-1">{t('findReturnByShk.created')}</span>
                      )}
                    </>
                  )}
                </div>
                {r.order_id && r.error !== 'deleted' && (
                  <div className="d-flex gap-1">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        onEditOrder(r.order_id);
                        handleClose();
                      }}
                    >
                      {t('findReturnByShk.viewEdit')}
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteOrder(r.order_id, r.shk_id)}
                      disabled={deletingId === r.order_id}
                    >
                      {deletingId === r.order_id ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        t('findReturnByShk.deleteOrder')
                      )}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--bs-border-color)' }}>
        <Button variant="outline-secondary" onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="warning"
          onClick={handleSubmit}
          disabled={loading || !parseShkIds(shkIdsText).length}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {t('findReturnByShk.searching')}
            </>
          ) : (
            t('findReturnByShk.submit')
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
